import { faChevronRight, faChevronLeft, faSpinner } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Form } from "react-bootstrap"
import React, { useState, useEffect, useContext, useRef, useMemo } from "react"
import { WingsEyebrow, WingsHeading, WingsText, WingsDetailText, WingsLink, WingsBadge, WingsSearchBar } from "@wingspans-ui"
import useStockModulesDB from "./shared/useStockModulesDB"

import { FirebaseContext } from "../Firebase"
import Fuse from "fuse.js"

import "./work-readiness.css"
import { FormattedMessage, useIntl } from "react-intl"
import { navigate } from "../../link"
import useSchoolFilteredProgramsDB from "./shared/useSchoolFilteredProgramsDB"
import { RadioButton } from "primereact/radiobutton"
import { truncateText } from "./shared/utils"
import DOMPurify from "dompurify"
import { Dropdown } from "semantic-ui-react"
import { ImageWithFallback } from "./views/display"
import useDashboardRegionKeyboardNavigation from "../hooks/useDashboardRegionKeyboardNavigation"
import useWindowDimensions from "../hooks/useWindowDimensions"

const ASSIGNMENT_SEARCH_KEYS = [
  { name: "title", weight: 0.6 },
  { name: "title_es", weight: 0.2 },
  { name: "category", weight: 0.1 },
  { name: "category_es", weight: 0.1 },
]

const MODULE_SEARCH_KEYS = [
  { name: "title", weight: 0.45 },
  { name: "title_es", weight: 0.2 },
  { name: "short_description", weight: 0.2 },
  { name: "short_description_es", weight: 0.1 },
  { name: "category", weight: 0.03 },
  { name: "category_es", weight: 0.02 },
]

const PROGRAM_SEARCH_KEYS = [
  { name: "title", weight: 0.45 },
  { name: "title_es", weight: 0.2 },
  { name: "description", weight: 0.2 },
  { name: "description_es", weight: 0.1 },
  { name: "category", weight: 0.03 },
  { name: "category_es", weight: 0.02 },
]

const normalizeText = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeText(item)).join(" ")
  }

  if (value && typeof value === "object") {
    return normalizeText(Object.values(value))
  }

  if (value === null || value === undefined) {
    return ""
  }

  return String(value).toLowerCase().trim()
}

const getValueByPath = (item, keyPath) => {
  if (!item || !keyPath) {
    return ""
  }

  return keyPath.split(".").reduce((acc, key) => {
    if (acc === null || acc === undefined) {
      return ""
    }

    return acc[key]
  }, item)
}

const matchesQueryTokensForKeys = (item, query, keys) => {
  const normalizedQuery = normalizeText(query)
  if (!normalizedQuery) {
    return true
  }

  const tokens = normalizedQuery.split(/\s+/).filter(Boolean)
  if (!tokens.length) {
    return true
  }

  const searchableText = normalizeText((keys || []).map((keyConfig) => {
    const keyPath = typeof keyConfig === "string" ? keyConfig : keyConfig?.name
    return getValueByPath(item, keyPath)
  }))

  return tokens.every((token) => searchableText.includes(token))
}

const getTitleMatchPriority = (item, query) => {
  const normalizedQuery = normalizeText(query)
  if (!normalizedQuery) {
    return 4
  }

  const normalizedTitle = normalizeText(item?.title)
  if (!normalizedTitle) {
    return 4
  }

  if (normalizedTitle === normalizedQuery) {
    return 0
  }

  const titleWords = normalizedTitle.split(/[^a-z0-9]+/).filter(Boolean)
  if (titleWords.includes(normalizedQuery)) {
    return 1
  }

  if (normalizedTitle.startsWith(normalizedQuery)) {
    return 2
  }

  if (normalizedTitle.includes(normalizedQuery)) {
    return 3
  }

  return 4
}

const searchCurriculumItems = (items = [], query, keys) => {
  const normalizedQuery = (query || "").trim()
  if (!normalizedQuery) {
    return items
  }

  if (normalizedQuery.length < 2) {
    return items
      .filter((item) => matchesQueryTokensForKeys(item, normalizedQuery, keys))
      .slice()
      .sort((a, b) => {
        const aPriority = getTitleMatchPriority(a, normalizedQuery)
        const bPriority = getTitleMatchPriority(b, normalizedQuery)
        if (aPriority !== bPriority) {
          return aPriority - bPriority
        }

        return String(a?.title || "").localeCompare(String(b?.title || ""))
      })
  }

  const fuseIndex = new Fuse(items, {
    keys,
    includeScore: true,
    threshold: 0.35,
    ignoreLocation: true,
    minMatchCharLength: 2,
  })

  const fuzzyResults = fuseIndex.search(normalizedQuery)
  const strictTokenResults = fuzzyResults.filter((result) => matchesQueryTokensForKeys(result.item, normalizedQuery, keys))
  const selectedResults = strictTokenResults.length === 0 ? fuzzyResults : strictTokenResults

  return selectedResults
    .slice()
    .sort((a, b) => {
      const aPriority = getTitleMatchPriority(a.item, normalizedQuery)
      const bPriority = getTitleMatchPriority(b.item, normalizedQuery)
      if (aPriority !== bPriority) {
        return aPriority - bPriority
      }

      const aScore = a.score ?? Number.POSITIVE_INFINITY
      const bScore = b.score ?? Number.POSITIVE_INFINITY
      if (aScore !== bScore) {
        return aScore - bScore
      }

      return String(a.item?.title || "").localeCompare(String(b.item?.title || ""))
    })
    .map((result) => result.item)
}

/**
 * Work Readiness tab component, displays either K-12 or Adult work readiness activities.
 *
 * @returns JSX component
 */
const WorkReadinessTab = () => {
  const { formatMessage } = useIntl()
  const { firebase } = useContext(FirebaseContext)
  const [curriculumType, setCurriculumType] = useState("MODULES") // Initially null to wait for assignments to load
  const [assignments, setAssignments] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        const res = await firebase.getModulesAssignedToUser() // Fetch assignments
        const fetchedAssignments = [...res?.data?.programAssignments, ...res?.data?.moduleAssignments] || []
        setAssignments(fetchedAssignments)

        // If there are active assignments, show "Assignments" tab first
        if (fetchedAssignments.length > 0) {
          setCurriculumType("ASSIGNMENTS")
        } else {
          setCurriculumType("MODULES")
        }
      } catch (error) {
        console.error("Error fetching assignments:", error)
        setCurriculumType("MODULES") // Default to "Programs" if there's an error
      } finally {
        setIsLoading(false)
      }
    }

    fetchAssignments()
  }, [firebase])

  return (
    <div className="work-readiness">
      <header className="work-readiness-header w-mb-2">
        <WingsEyebrow as="p">
          <FormattedMessage id="work-readiness-eyebrow" defaultMessage="BUILD" />
        </WingsEyebrow>
        <WingsHeading level={1}>
          <FormattedMessage id="work-readiness-heading" defaultMessage="Skills" />
        </WingsHeading>
        <WingsText className="w-mt-1 w-max-w-65ch w-mx-auto">
          <FormattedMessage
            id="work-readiness-overview-copy"
            defaultMessage="Prepare for success in the workplace! Activities are based on the NACE competencies."
          />
        </WingsText>
        

        
      </header>

      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "20vh" }}>
          <FontAwesomeIcon icon={faSpinner} spin size="3x" color="#00b8ef" />
        </div>
      ) : (
        <Curriculum curriculumType={curriculumType} setCurriculumType={setCurriculumType} assignments={assignments} />
      )}
    </div>
  )
}
const Curriculum = ({ curriculumType, setCurriculumType, assignments }) => {
  const { formatMessage } = useIntl()
  const { width } = useWindowDimensions()
  const useCompactTabLabels = width <= 640

  const stripLeadingAll = (value) => {
    if (!value) return value
    return value
      .replace(/^\s*(all)\s+/i, "")
      .replace(/^\s*(todos|todas)\s+(los|las)\s+/i, "")
      .trim()
  }

  const allModulesLabel = formatMessage({ id: "curriculum-modules", defaultMessage: "modules" })
  const allProgramsLabel = formatMessage({ id: "curriculum-programs", defaultMessage: "programs" })
  const modulesLabel = useCompactTabLabels ? stripLeadingAll(allModulesLabel) : allModulesLabel
  const programsLabel = useCompactTabLabels ? stripLeadingAll(allProgramsLabel) : allProgramsLabel

  useDashboardRegionKeyboardNavigation({
    isEnabled: true,
    regionOrder: ["curriculumTypeTabs", "curriculumFilters", "curriculumGrid", "curriculumPagination"],
    focusableSelectorsByRegion: {
      curriculumTypeTabs: "[data-keyboard-item='curriculum-type']",
      curriculumFilters: "input, button:not([disabled]), [tabindex]:not([tabindex='-1'])",
      curriculumGrid: "[data-keyboard-item='curriculum-card']",
      curriculumPagination: "button:not([disabled])",
    },
    shouldWrapOnBoundary: ({ key }) => key === "ArrowLeft" || key === "ArrowRight",
  })

  const handleCurriculumTypeKeyDown = (e, nextType) => {
    if (e.key !== "Enter" && e.key !== " ") return
    e.preventDefault()
    setCurriculumType(nextType)
  }

  return (
    <div className="curriculum">
      <div className="curriculum-header w-gap-1" data-keyboard-region="curriculumTypeTabs">
        <label
          className={curriculumType === "MODULES" ? "active" : ""}
          onClick={() => setCurriculumType("MODULES")}
          role="button"
          tabIndex={0}
          data-keyboard-item="curriculum-type"
          onKeyDown={(e) => handleCurriculumTypeKeyDown(e, "MODULES")}
        >
          {modulesLabel}
        </label>{" "}
        |
        <label
          className={curriculumType === "PROGRAMS" ? "active" : ""}
          onClick={() => setCurriculumType("PROGRAMS")}
          role="button"
          tabIndex={0}
          data-keyboard-item="curriculum-type"
          onKeyDown={(e) => handleCurriculumTypeKeyDown(e, "PROGRAMS")}
        >
          {programsLabel}
        </label>{" "}
        {assignments.length > 0 && (
          <>
            {" "}
            |
            <label
              className={curriculumType === "ASSIGNMENTS" ? "active" : ""}
              onClick={() => setCurriculumType("ASSIGNMENTS")}
              role="button"
              tabIndex={0}
              data-keyboard-item="curriculum-type"
              onKeyDown={(e) => handleCurriculumTypeKeyDown(e, "ASSIGNMENTS")}
            >
              <FormattedMessage id="curriculum-assignments" defaultMessage="assignments" />
            </label>
          </>
        )}
      </div>
      {curriculumType === "PROGRAMS" && <CurriculumPrograms assignments={assignments} />}
      {curriculumType === "ASSIGNMENTS" && <CurriculumAssignments assignments={assignments} />}
      {curriculumType === "MODULES" && <CurriculumModules assignments={assignments} />}
    </div>
  )
}

const CurriculumAssignments = ({ assignments }) => {
  const { formatMessage, locale } = useIntl()
  const [search, setSearch] = useState("")
  const [category, setCategory] = useState("ALL")
  const [completionStatusSort, setCompletionStatusSort] = useState("ALL")
  const [currentPage, setCurrentPage] = useState(1)
  const scrollRef = useRef(null) // Create a ref for scrolling
  const itemsPerPage = 12

  const searchedAssignments = useMemo(() => {
    return searchCurriculumItems(assignments, search, ASSIGNMENT_SEARCH_KEYS)
  }, [assignments, search])

  let filteredAssignments = searchedAssignments

  if (category !== "ALL") {
    filteredAssignments = filteredAssignments.filter((program) => program.category === category)
  }

  if (completionStatusSort !== "ALL") {
    filteredAssignments = filteredAssignments.filter((program) => program.status === completionStatusSort)
  }

  const uniqueCategories = ["ALL", ...new Set(assignments.map((assignment) => assignment.category).filter(category => category))].sort((a, b) => {
    if (a === "ALL") return -1
    if (b === "ALL") return 1
    return a.localeCompare(b)
  })

  const categoryToText = { "ALL": formatMessage({ id: "curriculum-ALL", defaultMessage: "ALL" }) }
  filteredAssignments.forEach(assignment => {
    if (locale === "es-ES" && assignment.category && assignment.category_es) {
      categoryToText[assignment.category] = assignment.category_es
    } else if (locale === "en-US" && assignment.category) {
      categoryToText[assignment.category] = assignment.category
    }
  })

  const renderCurriculum = () => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const currentAssignments = filteredAssignments.slice(startIndex, endIndex)

    return currentAssignments.map((assignment, index) => <CurriculumAssignmentCard key={index} assignment={assignment} />)
  }

  const handleCategoryChange = (event) => {
    setCategory(event.target.value)
    setCompletionStatusSort("ALL")
    setSearch("")
    setCurrentPage(1)
  }

  const handleCompletionStatusSortChange = (status) => {
    setSearch("") // Clear the search input field
    setCategory("ALL") // Reset the category to "ALL"
    setCompletionStatusSort(status) // Update the completion status sort
    setCurrentPage(1) // Reset to the first page
  }

  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage(currentPage - 1)
    } else if (direction === "next" && currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }

    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    }, 100)
  }

  const totalPages = Math.ceil(filteredAssignments.length / itemsPerPage)
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, filteredAssignments.length)

  return (
    <>
      <div className="curriculum-filters w-full w-gap-1 w-mt-1 w-justify-between w-items-center" ref={scrollRef} data-keyboard-region="curriculumFilters">
        <Dropdown
          placeholder="Select Category"
          selection
          options={uniqueCategories.map((cat) => ({
            key: cat,
            text: categoryToText[cat] || cat,
            value: cat,
          }))}
          icon="chevron down"
          value={category}
          onChange={(e, { value }) => handleCategoryChange({ target: { value } })}
        />
        <div className="radio-group">
          <span className="curriculum-sort-by-label">
            <FormattedMessage id="curriculum-sort-by" defaultMessage="Sort by:" />
          </span>
          <div>
            <label>
              <RadioButton
                inputId="all"
                name="choice"
                value="ALL"
                checked={completionStatusSort === "ALL"}
                onChange={() => handleCompletionStatusSortChange("ALL")}
              />
              <span>
                <FormattedMessage id="curriculum-all" defaultMessage="All" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="in_progress"
                name="choice"
                value="in_progress"
                checked={completionStatusSort === "in_progress"}
                onChange={() => handleCompletionStatusSortChange("in_progress")}
              />
              <span>
                <FormattedMessage id="curriculum-in-progress" defaultMessage="In Progress" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="completed"
                name="choice"
                value="completed"
                checked={completionStatusSort === "completed"}
                onChange={() => handleCompletionStatusSortChange("completed")}
              />
              <span>
                <FormattedMessage id="curriculum-completed" defaultMessage="Completed" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="not_started"
                name="choice"
                value="not_started"
                checked={completionStatusSort === "not_started"}
                onChange={() => handleCompletionStatusSortChange("not_started")}
              />
              <span>
                <FormattedMessage id="curriculum-not-started" defaultMessage="Not Started" />
              </span>
            </label>
          </div>
        </div>
        <WingsSearchBar
          className="search w-m-0"
          placeholder={formatMessage({ id: "curriculum-module-name", defaultMessage: "Module Name" })}
          value={search}
          onChange={setSearch}
        />
      </div>

      <div className="curriculum-content-grid" data-keyboard-region="curriculumGrid">{renderCurriculum()}</div>
      <div className="pagination" data-keyboard-region="curriculumPagination">
        <button onClick={() => handlePageChange("prev")} disabled={currentPage === 1}>
          <FontAwesomeIcon icon={faChevronLeft} color="#406C70" />
        </button>
        <span>
          {startItem} - {endItem} <FormattedMessage id="curriculum-of" defaultMessage="of" /> {filteredAssignments.length}
        </span>
        <button onClick={() => handlePageChange("next")} disabled={currentPage === totalPages}>
          <FontAwesomeIcon icon={faChevronRight} color="#406C70" />
        </button>
      </div>
    </>
  )
}

const CurriculumModules = ({ assignments = [] }) => {
  const { firebase, subdomainConfig } = useContext(FirebaseContext)
  const { formatMessage, locale } = useIntl()
  const { stockModules, isLoading, error } = useStockModulesDB(
    firebase,
    subdomainConfig?.subdomainAudienceType,
    subdomainConfig?.subdomainSchoolId
  )
  const scrollRef = useRef(null) // Create a ref for the Curriculum component
  const [search, setSearch] = useState("")
  const [category, setCategory] = useState("ALL")
  const [completionStatusSort, setCompletionStatusSort] = useState("ALL")
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 12

  const searchedModules = useMemo(() => {
    return searchCurriculumItems(stockModules || [], search, MODULE_SEARCH_KEYS)
  }, [stockModules, search])

  let filteredModules = searchedModules

  if (category !== "ALL") {
    filteredModules = filteredModules.filter((module) => module.category === category)
  }

  if (completionStatusSort !== "ALL") {
    filteredModules = filteredModules.filter((module) => module.status === completionStatusSort)
  }

  const uniqueCategories = ["ALL", ...new Set((stockModules || []).map((module) => module.category).filter(category => category))].sort((a, b) => {
    if (a === "ALL") return -1;
    if (b === "ALL") return 1;
    const numA = parseInt(a);
    const numB = parseInt(b);
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }
    if (!isNaN(numA)) {
      return -1;
    }
    if (!isNaN(numB)) {
      return 1;
    }
    return a.localeCompare(b);
  });

  const categoryToText = { "ALL": formatMessage({ id: "curriculum-ALL", defaultMessage: "ALL" }) }
  filteredModules.forEach(module => {
    if (locale === "es-ES" && module.category && module.category_es) {
      categoryToText[module.category] = module.category_es
    } else if (locale === "en-US" && module.category) {
      categoryToText[module.category] = module.category
    }
  })

  const assignedModuleIds = new Set(
    assignments
      .map((assignment) => assignment?.module_id ?? assignment?.moduleId ?? assignment?.module?.id)
      .filter(Boolean)
  )

  const renderModules = () => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const currentModules = filteredModules.slice(startIndex, endIndex)

    return currentModules.map((module, index) => (
      <CurriculumModuleCard
        key={index}
        module={{
          ...module,
          isAssigned:
            module?.isAssigned ||
            module?.is_assigned ||
            assignedModuleIds.has(module?.id) ||
            assignedModuleIds.has(module?.module_id),
        }}
      />
    ))
  }

  const handleCategoryChange = (event) => {
    setCategory(event.target.value)
    setCompletionStatusSort("ALL")
    setSearch("")
    setCurrentPage(1)
  }

  const handleCompletionStatusSortChange = (status) => {
    setSearch("") // Clear the search input field
    setCategory("ALL") // Reset the category to "ALL" 
    setCompletionStatusSort(status) // Update the completion status sort
    setCurrentPage(1) // Reset to the first page
  }

  const handlePageChange = (direction) => {
    // Scroll to the top of the Curriculum component
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage(currentPage - 1)
    } else if (direction === "next" && currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }

    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    }, 100)
  }

  const totalPages = Math.ceil(filteredModules.length / itemsPerPage)
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, filteredModules.length)

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "20vh" }}>
        <FontAwesomeIcon icon={faSpinner} spin size="3x" color="#00b8ef" />
      </div>
    )
  }

  if (error) {
    return <div>Error loading modules: {error.message}</div>
  }

  return (
    <>
      <div className="curriculum-filters w-full w-gap-1 w-mt-1 w-justify-between w-items-center" ref={scrollRef} data-keyboard-region="curriculumFilters">
        <Dropdown
          placeholder="Select Category"
          selection
          options={uniqueCategories.map((cat) => ({
            key: cat,
            text: categoryToText[cat] || cat,
            value: cat,
          }))}
          icon="chevron down"
          value={category}
          onChange={(e, { value }) => handleCategoryChange({ target: { value } })}
        />
        <div className="radio-group">
          <span className="curriculum-sort-by-label">
            <FormattedMessage id="curriculum-sort-by" defaultMessage="Sort by:" />
          </span>
          <div>
            <label>
              <RadioButton
                inputId="all"
                name="choice"
                value="ALL"
                checked={completionStatusSort === "ALL"}
                onChange={() => handleCompletionStatusSortChange("ALL")}
              />
              <span>
                <FormattedMessage id="curriculum-all" defaultMessage="All" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="in_progress"
                name="choice"
                value="in_progress"
                checked={completionStatusSort === "in_progress"}
                onChange={() => handleCompletionStatusSortChange("in_progress")}
              />
              <span>
                <FormattedMessage id="curriculum-in-progress" defaultMessage="In Progress" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="completed"
                name="choice"
                value="completed"
                checked={completionStatusSort === "completed"}
                onChange={() => handleCompletionStatusSortChange("completed")}
              />
              <span>
                <FormattedMessage id="curriculum-completed" defaultMessage="Completed" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="not_started"
                name="choice"
                value="not_started"
                checked={completionStatusSort === "not_started"}
                onChange={() => handleCompletionStatusSortChange("not_started")}
              />
              <span>
                <FormattedMessage id="curriculum-not-started" defaultMessage="Not Started" />
              </span>
            </label>
          </div>
        </div>
        <WingsSearchBar
          className="search w-m-0"
          placeholder={formatMessage({ id: "curriculum-module-name", defaultMessage: "Module Name" })}
          value={search}
          onChange={setSearch}
        />
      </div>

      <div className="curriculum-content-grid" data-keyboard-region="curriculumGrid">{renderModules()}</div>
      <div className="pagination" data-keyboard-region="curriculumPagination">
        <button onClick={() => handlePageChange("prev")} disabled={currentPage === 1}>
          <FontAwesomeIcon icon={faChevronLeft} color="#406C70" />
        </button>
        <span>
          {startItem} - {endItem} <FormattedMessage id="curriculum-of" defaultMessage="of" /> {filteredModules.length}
        </span>
        <button onClick={() => handlePageChange("next")} disabled={currentPage === totalPages}>
          <FontAwesomeIcon icon={faChevronRight} color="#406C70" />
        </button>
      </div>
    </>
  )
}

const CurriculumModuleCard = ({ module }) => {
  const { short_description, short_description_es, id, title, title_es, category, category_es, time, time_es, status, image, skills, isAssigned } = module

  const { locale } = useIntl()

  // Localize all displayed text
  let localizedTitle, localizedDescription, localizedCategory, localizedTime
  if (locale === "en-US" || !locale) {
    localizedTitle = title
    localizedDescription = short_description
    localizedCategory = category
    localizedTime = time
  } else if (locale === "es-ES") {
    localizedTitle = title_es || title
    localizedDescription = short_description_es || short_description
    localizedCategory = category_es || category
    localizedTime = time_es || time
  }

  const renderDescription = () => DOMPurify.sanitize(localizedDescription ?? "")

  const handleCardClick = () => {
    navigate(`${locale === "es-ES" ? "/es-ES" : ""}/modules/${id}`)
  }

  const handleCardKeyDown = (e) => {
    if (e.key !== "Enter" && e.key !== " ") return
    e.preventDefault()
    handleCardClick()
  }

  const showProgressBadge = status === "in_progress" || status === "completed"

  return (
    <div
      className="curriculum-assignment-card"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      data-keyboard-item="curriculum-card"
      onKeyDown={handleCardKeyDown}
    >
      <div className="curriculum-assignment-card-floating-badges">
        {isAssigned && (
          <WingsBadge className="curriculum-assignment-card-floating-badge curriculum-assignment-card-floating-badge-assigned" variant="primary">
            <FormattedMessage id="curriculum-assigned" defaultMessage="Assigned" />
          </WingsBadge>
        )}
        {showProgressBadge && (
          <WingsBadge
            className={`curriculum-assignment-card-floating-badge ${status === "completed" ? "curriculum-assignment-card-floating-badge-completed" : "curriculum-assignment-card-floating-badge-progress"}`}
            variant="tertiary"
          >
            <FormattedMessage
              id={status === "completed" ? "curriculum-completed" : "curriculum-in-progress"}
              defaultMessage={status === "completed" ? "Completed" : "In Progress"}
            />
          </WingsBadge>
        )}
      </div>
      <div className="curriculum-assignment-card-img">
        <ImageWithFallback
          src={image?.length > 0 ? image : "https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/assessment-assets%2Fshutterstock_1501576922.jpg?alt=media&token=85415939-70ed-4213-bdbc-ba3a4d616797"}
          alt={localizedTitle ? `${localizedTitle} decorative module image` : "Decorative module image"}
        />
      </div>

      <div className="curriculum-assignment-card-content w-flex w-flex-column w-flex-1 w-py-1">
        {(localizedCategory || localizedTime) && (
          <div className="curriculum-assignment-card-meta w-flex w-justify-between w-items-center">
            {localizedCategory && (
              <WingsDetailText as="span" className="curriculum-assignment-card-category w-mb-0">
                {localizedCategory}
              </WingsDetailText>
            )}
            {localizedTime && (
              <WingsDetailText as="span" className="curriculum-assignment-card-meta-time">{localizedTime}</WingsDetailText>
            )}
          </div>
        )}

        <WingsLink
          to={`/modules/${id}`}
          size="h5"
          display="block"
          className="curriculum-assignment-card-title-link"
        >
          {localizedTitle}
        </WingsLink>

        <WingsDetailText as="div" className="curriculum-assignment-card-description w-mb-1">
          {renderDescription()}
        </WingsDetailText>

                {skills?.length > 0 && (
          <div className="curriculum-assignment-card-skills w-mb-1">
            <div className="curriculum-assignment-card-skills-tags w-flex w-flex-wrap">
              {skills.map((skill, i) => (
                // <WingsBadge key={i} variant="soft-skill">{skill}</WingsBadge>
                <div className="curriculum-assignment-card-skill-tag">
                <WingsDetailText key={i}>{skill}</WingsDetailText>

                  </div>
              ))}
            </div>
          </div>
        )}
      </div>

    </div>
  )
}

const CurriculumPrograms = ({ assignments = [] }) => {
  const { formatMessage, locale } = useIntl()
  const [search, setSearch] = useState("")
  const [category, setCategory] = useState("ALL")
  const [completionStatusSort, setCompletionStatusSort] = useState("ALL")
  const scrollRef = useRef(null) // Create a ref for scrolling
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 12

  const { firebase, subdomainConfig } = useContext(FirebaseContext)

  const { programListWithUserCompletion, areProgramsLoading } = useSchoolFilteredProgramsDB(firebase, subdomainConfig.subdomainSchoolId, subdomainConfig?.subdomainAudienceType)

  const searchedPrograms = useMemo(() => {
    return searchCurriculumItems(programListWithUserCompletion, search, PROGRAM_SEARCH_KEYS)
  }, [programListWithUserCompletion, search])

  let filteredPrograms = searchedPrograms

  if (category !== "ALL") {
    filteredPrograms = filteredPrograms.filter((program) => program.category === category)
  }

  if (completionStatusSort !== "ALL") {
    filteredPrograms = filteredPrograms.filter((program) => program.status === completionStatusSort)
  }

  const uniqueCategories = ["ALL", ...new Set(programListWithUserCompletion.map((program) => program.category).filter(category => category))].sort((a, b) => {
    if (a === "ALL") return -1
    if (b === "ALL") return 1
    return a.localeCompare(b)
  })

  const categoryToText = { "ALL": formatMessage({ id: "curriculum-ALL", defaultMessage: "ALL" }) }
  programListWithUserCompletion.forEach(program => {
    if (locale === "es-ES" && program.category && program.category_es) {
      categoryToText[program.category] = program.category_es
    } else if (locale === "en-US" && program.category) {
      categoryToText[program.category] = program.category
    }
  })

  const assignedProgramIds = new Set(
    assignments
      .map((assignment) => assignment?.program_id ?? assignment?.programId ?? assignment?.program?.id)
      .filter(Boolean)
  )

  const renderCurriculum = () => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const currentPrograms = filteredPrograms.slice(startIndex, endIndex)

    return currentPrograms.map((program, index) => (
      <CurriculumProgramCard
        key={index}
        program={{
          ...program,
          isAssigned:
            program?.isAssigned ||
            program?.is_assigned ||
            assignedProgramIds.has(program?.id) ||
            assignedProgramIds.has(program?.program_id),
        }}
      />
    ))
  }

  const handleCurriculumTypeChange = (type) => {
    setSearch("")
    setCategory("ALL")
    setCompletionStatusSort("ALL")
    // setCurriculumType(type)
    setCurrentPage(1)
  }

  const handleCategoryChange = (event) => {
    setCategory(event.target.value)
    setCompletionStatusSort("ALL")
    setSearch("")
    setCurrentPage(1)
  }

  const handleCompletionStatusSortChange = (status) => {
    setSearch("") // Clear the search input field
    setCategory("ALL") // Reset the category to "ALL"
    setCompletionStatusSort(status) // Update the completion status sort
    setCurrentPage(1) // Reset to the first page
  }

  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage(currentPage - 1)
    } else if (direction === "next" && currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }

    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    }, 100)
  }

  const totalPages = Math.ceil(filteredPrograms.length / itemsPerPage)
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, filteredPrograms.length)

  return (
    <>
      <div className="curriculum-filters w-full w-gap-1 w-mt-1 w-justify-between w-items-center" ref={scrollRef} data-keyboard-region="curriculumFilters">
        <Dropdown
          placeholder="Select Category"
          selection
          options={uniqueCategories.map((cat) => ({
            key: cat,
            text: categoryToText[cat] || cat,
            value: cat,
          }))}
          icon="chevron down"
          value={category}
          onChange={(e, { value }) => handleCategoryChange({ target: { value } })}
        />
        <div className="radio-group">
          <span className="curriculum-sort-by-label">
            <FormattedMessage id="curriculum-sort-by" defaultMessage="Sort by:" />
          </span>
          <div>
            <label>
              <RadioButton
                inputId="all"
                name="choice"
                value="ALL"
                checked={completionStatusSort === "ALL"}
                onChange={() => handleCompletionStatusSortChange("ALL")}
              />
              <span>
                <FormattedMessage id="curriculum-all" defaultMessage="All" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="in_progress"
                name="choice"
                value="in_progress"
                checked={completionStatusSort === "in_progress"}
                onChange={() => handleCompletionStatusSortChange("in_progress")}
              />
              <span>
                <FormattedMessage id="curriculum-in-progress" defaultMessage="In Progress" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="completed"
                name="choice"
                value="completed"
                checked={completionStatusSort === "completed"}
                onChange={() => handleCompletionStatusSortChange("completed")}
              />
              <span>
                <FormattedMessage id="curriculum-completed" defaultMessage="Completed" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="not_started"
                name="choice"
                value="not_started"
                checked={completionStatusSort === "not_started"}
                onChange={() => handleCompletionStatusSortChange("not_started")}
              />
              <span>
                <FormattedMessage id="curriculum-not-started" defaultMessage="Not Started" />
              </span>
            </label>
          </div>
        </div>
        <WingsSearchBar
          className="search w-m-0"
          placeholder={formatMessage({ id: "curriculum-program-name", defaultMessage: "Program Name" })}
          value={search}
          onChange={setSearch}
        />
      </div>
      {areProgramsLoading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "20vh" }}>
          <FontAwesomeIcon icon={faSpinner} spin size="3x" color="#00b8ef" />
        </div>
      ) : (
        <div className="curriculum-content-grid" data-keyboard-region="curriculumGrid">{renderCurriculum()}</div>
      )}
      <div className="pagination" data-keyboard-region="curriculumPagination">
        <button onClick={() => handlePageChange("prev")} disabled={currentPage === 1}>
          <FontAwesomeIcon icon={faChevronLeft} color="#406C70" />
        </button>
        <span>
          {startItem} - {endItem} <FormattedMessage id="curriculum-of" defaultMessage="of" /> {filteredPrograms.length}
        </span>
        <button onClick={() => handlePageChange("next")} disabled={currentPage === totalPages}>
          <FontAwesomeIcon icon={faChevronRight} color="#406C70" />
        </button>
      </div>
    </>
  )
}

const CurriculumProgramCard = ({ program }) => {
  const { id, title, title_es, description, description_es, category, category_es, time, time_es, image, status, isAssigned } = program
  const { locale } = useIntl()

  // Localize all displayed text
  let localizedTitle, localizedDescription, localizedCategory, localizedTime
  if (locale === "en-US" || !locale) {
    localizedTitle = title
    localizedDescription = description
    localizedCategory = category
    localizedTime = time
  } else if (locale === "es-ES") {
    localizedTitle = title_es || title
    localizedDescription = description_es || description
    localizedCategory = category_es || category
    localizedTime = time_es || time
  }

  const renderDescription = () => DOMPurify.sanitize(localizedDescription ?? "")

  const handleCardClick = () => {
    navigate(`${locale === "es-ES" ? "/es-ES" : ""}/programs/${id}`)
  }

  const handleCardKeyDown = (e) => {
    if (e.key !== "Enter" && e.key !== " ") return
    e.preventDefault()
    handleCardClick()
  }

  const showProgressBadge = status === "in_progress" || status === "completed"

  return (
    <div
      className="curriculum-assignment-card"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      data-keyboard-item="curriculum-card"
      onKeyDown={handleCardKeyDown}
    >
      <div className="curriculum-assignment-card-floating-badges">
        {isAssigned && (
          <WingsBadge className="curriculum-assignment-card-floating-badge curriculum-assignment-card-floating-badge-assigned" variant="primary">
            <FormattedMessage id="curriculum-assigned" defaultMessage="Assigned" />
          </WingsBadge>
        )}
        {showProgressBadge && (
          <WingsBadge
            className={`curriculum-assignment-card-floating-badge ${status === "completed" ? "curriculum-assignment-card-floating-badge-completed" : "curriculum-assignment-card-floating-badge-progress"}`}
            variant="tertiary"
          >
            <FormattedMessage
              id={status === "completed" ? "curriculum-completed" : "curriculum-in-progress"}
              defaultMessage={status === "completed" ? "Completed" : "In Progress"}
            />
          </WingsBadge>
        )}
      </div>
      <div className="curriculum-assignment-card-img">
        <ImageWithFallback
          src={image ?? "https://imgix.cosmicjs.com/73afba70-598a-11ef-b1ea-f56c65dfade9-unnamed-12.jpg"}
          alt={localizedTitle ? `${localizedTitle} decorative program image` : "Decorative program image"}
        />
      </div>

      <div className="curriculum-assignment-card-content w-flex w-flex-column w-flex-1 w-py-1">
        {(localizedCategory || localizedTime) && (
          <div className="curriculum-assignment-card-meta w-flex w-justify-between w-items-center">
            {localizedCategory && (
              <WingsDetailText as="span" className="curriculum-assignment-card-category w-mb-0">
                {localizedCategory}
              </WingsDetailText>
            )}
            {localizedTime && (
              <WingsDetailText as="span" className="curriculum-assignment-card-meta-time">{localizedTime}</WingsDetailText>
            )}
          </div>
        )}

        <WingsLink
          to={`/programs/${id}`}
          size="h5"
          display="block"
          className="curriculum-assignment-card-title-link"
        >
          {localizedTitle}
        </WingsLink>

        <WingsDetailText as="div" className="curriculum-assignment-card-description w-mb-1">
          {renderDescription()}
        </WingsDetailText>
      </div>

    </div>
  )
}

const CurriculumAssignmentCard = ({ assignment }) => {
  const {
    assignment_type,
    short_description,
    short_description_es,
    module_id,
    program_id,
    title,
    title_es,
    category,
    category_es,
    time,
    time_es,
    status,
    image,
    due_at,
    creator_first_name,
    creator_last_name,
  } = assignment

  const { locale } = useIntl()
  const formattedDueDate = new Date(due_at).toLocaleDateString(locale)

  // Localize all displayed text
  let localizedTitle, localizedDescription, localizedCategory, localizedTime
  if (locale === "en-US" || !locale) {
    localizedTitle = title
    localizedDescription = short_description
    localizedCategory = category
    localizedTime = time
  } else if (locale === "es-ES") {
    localizedTitle = title_es || title
    localizedDescription = short_description_es || short_description
    localizedCategory = category_es || category
    localizedTime = time_es || time
  }

  const renderDescription = () => DOMPurify.sanitize(localizedDescription ?? "")

  const calculateDaysRemaining = () => {
    if (!due_at) return null
    const today = new Date()
    const dueDate = new Date(due_at)
    const timeDiff = dueDate - today
    const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24))
    return daysRemaining
  }

  const daysRemaining = calculateDaysRemaining()

  const getDaysRemainingLabelStyle = () => {
    if (daysRemaining === null) return {}
    if (daysRemaining < 0) {
      return { backgroundColor: "#FFC2C2", color: "#363636" }
    } else if (daysRemaining <= 3) {
      return { backgroundColor: "#fff3c2", color: "#363636" }
    } else {
      return { backgroundColor: "#E5E5E5", color: "#363636" }
    }
  }

  const renderDaysRemainingLabel = () => {
    if (daysRemaining === null) return null
    if (daysRemaining < 0) {
      return (
        <span style={getDaysRemainingLabelStyle()}>
          <FormattedMessage id="curriculum-past-due" defaultMessage="Past Due" />
        </span>
      )
    } else if (daysRemaining === 0) {
      return (
        <span style={getDaysRemainingLabelStyle()}>
          <FormattedMessage id="curriculum-due-today" defaultMessage="Due Today" />
        </span>
      )
    } else if (daysRemaining === 1) {
      return (
        <span style={getDaysRemainingLabelStyle()}>
          <FormattedMessage id="curriculum-1-day-remaining" defaultMessage="1 Day Remaining" />
        </span>
      )
    } else {
      return (
        <span style={getDaysRemainingLabelStyle()}>
          {`${daysRemaining} `}<FormattedMessage id="curriculum-days-remaining" defaultMessage="Days Remaining" />
        </span>
      )
    }
  }

  const handleCardClick = () => {
    if (assignment_type === "program") {
      navigate(`${locale === "es-ES" ? "/es-ES" : ""}/programs/${program_id}`)
    } else {
      navigate(`${locale === "es-ES" ? "/es-ES" : ""}/modules/${module_id}`)
    }
  }

  const handleCardKeyDown = (e) => {
    if (e.key !== "Enter" && e.key !== " ") return
    e.preventDefault()
    handleCardClick()
  }

  return (
    <div
      className="curriculum-assignment-card"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      data-keyboard-item="curriculum-card"
      onKeyDown={handleCardKeyDown}
    >
      <div className="curriculum-assignment-card-floating-badges">
        <WingsBadge className="curriculum-assignment-card-floating-badge curriculum-assignment-card-floating-badge-assigned" variant="primary">
          <FormattedMessage id="curriculum-assigned" defaultMessage="Assigned" />
        </WingsBadge>
        {(status === "in_progress" || status === "completed") && (
          <WingsBadge
            className={`curriculum-assignment-card-floating-badge ${status === "completed" ? "curriculum-assignment-card-floating-badge-completed" : "curriculum-assignment-card-floating-badge-progress"}`}
            variant="tertiary"
          >
            <FormattedMessage
              id={status === "completed" ? "curriculum-completed" : "curriculum-in-progress"}
              defaultMessage={status === "completed" ? "Completed" : "In Progress"}
            />
          </WingsBadge>
        )}
      </div>
      <div className="curriculum-assignment-card-img">
        <ImageWithFallback src={image ?? "https://imgix.cosmicjs.com/73afba70-598a-11ef-b1ea-f56c65dfade9-unnamed-12.jpg"} alt={localizedTitle ? `${localizedTitle} decorative assignment image` : 'Decorative assignment image'} />
      </div>

      <div className="curriculum-assignment-card-content w-flex w-flex-column w-flex-1 w-py-1">
        {(localizedCategory || localizedTime) && (
          <div className="curriculum-assignment-card-meta w-flex w-justify-between w-items-center">
            {localizedCategory && (
              <WingsDetailText as="span" className="curriculum-assignment-card-category w-mb-0">
                {localizedCategory}
              </WingsDetailText>
            )}
            {localizedTime && (
              <WingsDetailText as="span" className="curriculum-assignment-card-meta-time">{localizedTime}</WingsDetailText>
            )}
          </div>
        )}

        <WingsLink
          to={`/${assignment_type === 'program' ? 'programs' : 'modules'}/${assignment_type === 'program' ? program_id : module_id}`}
          size="h5"
          display="block"
          className="curriculum-assignment-card-title-link"
        >
          {localizedTitle}
        </WingsLink>

        <WingsDetailText as="div" className="curriculum-assignment-card-description w-flex-1 w-mb-1">
          {renderDescription()}
        </WingsDetailText>

        {assignment.skills?.length > 0 && (
          <div className="curriculum-assignment-card-skills w-mb-1">
            <WingsDetailText as="span" className="curriculum-assignment-card-skills-label">
              <FormattedMessage id="curriculum-skills-earned" defaultMessage="Skills Earned:" />
            </WingsDetailText>
            <div className="curriculum-assignment-card-skills-tags w-flex w-flex-wrap">
              {assignment.skills.map((skill, i) => (
                <WingsDetailText key={i} as="span" className="curriculum-assignment-card-skill-tag">{skill}</WingsDetailText>
              ))}
            </div>
          </div>
        )}

        {(creator_first_name || creator_last_name || due_at) && (
          <div className="curriculum-assignment-card-assignment-details w-flex w-flex-column w-mb-1">
            {(creator_first_name || creator_last_name) && (
              <WingsDetailText as="span">
                <FormattedMessage id="curriculum-assigned-by" defaultMessage="Assigned by:" />{" "}
                <b>{creator_first_name ?? ""} {creator_last_name ?? ""}</b>
              </WingsDetailText>
            )}
            {due_at && (
              <>
                <WingsDetailText as="span" className="curriculum-assignment-card-due-date">
                  <FormattedMessage id="curriculum-due-date" defaultMessage="Due Date:" /> <b>{formattedDueDate}</b>
                </WingsDetailText>
                {status !== "completed" && daysRemaining !== null && <div className="days-remaining-label">{renderDaysRemainingLabel()}</div>}
              </>
            )}
          </div>
        )}
      </div>

    </div>
  )
}

export default WorkReadinessTab
